/**
 * THE TWO REQUESTS THIS SCREEN MAKES DIRECTLY, AND WHY THEY ARE NOT IN lib/api.ts.
 *
 * `lib/api.ts` deliberately reduces every failure to ONE sentence — that is the right call
 * for eleven screens where a 422 means "fix the highlighted field". This screen is the
 * exception: a graph that will not compile fails with a LIST of problems, each anchored to a
 * node, and turning that into one sentence would throw away the only thing that makes the
 * canvas usable (the message landing on the offending card). So the save goes through here,
 * where the 422 body survives.
 *
 * Everything else about the transport is copied from lib/api.ts on purpose — same-origin,
 * `credentials: 'include'`, the console's X-Requested-With marker — so this call is
 * indistinguishable from the rest of the console in the audit log.
 *
 * The situations request is here for a smaller reason: the mode list belongs to
 * /api/situations, which lib/api.ts does not wrap yet, and the `arm` node needs real keys
 * rather than a list this file made up. If it fails, the shipped catalogue in model.ts stands
 * in — an owner picking a mode is not a good moment to show an empty select (doctrine 9).
 */
import type { FlowDetail, SaveFlowRequest } from '../../lib/api.ts';
import type { CompileIssue } from './readthrough.ts';
import { FALLBACK_SITUATIONS } from './model.ts';
import type { SituationOption } from './Inspector.tsx';

const HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'nightdesk-console',
};

export type SaveOutcome =
  | { ok: true; flow: FlowDetail }
  /** The graph was refused. `steps` was NOT written — the phone still runs the old flow. */
  | { ok: false; kind: 'compile'; detail: string; issues: CompileIssue[]; warnings: CompileIssue[] }
  /** Anything else: a 403, a 409, the network. One sentence is the right answer for these. */
  | { ok: false; kind: 'other'; detail: string; status: number };

export async function saveFlowGraph(id: string, body: SaveFlowRequest): Promise<SaveOutcome> {
  let response: Response;
  try {
    response = await fetch(`/api/flows/${encodeURIComponent(id)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: HEADERS,
      body: JSON.stringify(body),
    });
  } catch {
    return {
      ok: false,
      kind: 'other',
      status: 0,
      detail:
        'The console could not reach NightDesk, so nothing was saved. Your drawing is still here — try again when the connection is back.',
    };
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = null;
    }
  }

  if (response.ok) {
    // The route answers `{ item, warnings }`. Older builds answered the flow directly, and a
    // canvas that renders an empty graph because the envelope changed shape is worse than one
    // that reads either.
    const envelope = (payload ?? {}) as { item?: unknown };
    const flow = (envelope.item ?? payload) as FlowDetail;
    return { ok: true, flow };
  }

  const shape = (payload ?? {}) as {
    error?: unknown;
    detail?: unknown;
    issues?: unknown;
    warnings?: unknown;
  };
  const detail = typeof shape.detail === 'string' ? shape.detail : '';

  if (response.status === 422) {
    return {
      ok: false,
      kind: 'compile',
      detail: detail || 'This flow will not compile yet, so it was not saved. The phone is still running the last version that did.',
      issues: asIssues(shape.issues),
      warnings: asIssues(shape.warnings),
    };
  }

  return {
    ok: false,
    kind: 'other',
    status: response.status,
    detail:
      detail ||
      (response.status === 403
        ? 'Your role does not allow changing call flows. An owner or an admin can.'
        : response.status === 409
          ? 'Somebody else saved this flow while you were drawing. Reload to see their version before you save over it.'
          : `The save failed (${response.status}). Nothing was changed.`),
  };
}

function asIssues(raw: unknown): CompileIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: CompileIssue[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      out.push({ nodeId: null, message: item });
      continue;
    }
    const issue = item as { nodeId?: unknown; message?: unknown };
    if (typeof issue.message !== 'string') continue;
    out.push({ nodeId: typeof issue.nodeId === 'string' ? issue.nodeId : null, message: issue.message });
  }
  return out;
}

// ------------------------------------------------------------------------------ situations

export async function fetchSituations(): Promise<SituationOption[]> {
  try {
    const response = await fetch('/api/situations', {
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-Requested-With': 'nightdesk-console' },
    });
    if (!response.ok) return FALLBACK_SITUATIONS;
    const payload = (await response.json()) as { situations?: unknown };
    const list = Array.isArray(payload.situations) ? payload.situations : [];
    const clean: SituationOption[] = [];
    for (const raw of list) {
      const s = raw as { key?: unknown; label?: unknown; kind?: unknown };
      if (typeof s.key !== 'string' || typeof s.label !== 'string') continue;
      clean.push({ key: s.key, label: s.label, kind: s.kind === 'overlay' ? 'overlay' : 'posture' });
    }
    return clean.length > 0 ? clean : FALLBACK_SITUATIONS;
  } catch {
    return FALLBACK_SITUATIONS;
  }
}
